/*
 * Copyright (c) 2023 Bluemate B.V.
 */

import {NumberString, roundToNumber} from '@dvrd/dvr-controls';

import {SplitVATValue, VATType, VATValue} from '../util/interfaces';

import {deepClone, ensureNegative, roundNumberString, sum} from '../util/utils';

import {calculateExclVAT, calculateVATAmount, DefaultVAT} from '../util/vatUtil';

import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';

import Invoice from './invoiceModel';

import {BolDiscount} from '../util/types';

interface Props extends BaseProps {
    item_id?: string;
    ean?: string;
    title?: string;
    quantity?: number;
    quantity_shipped?: number;
    quantity_cancelled?: number;
    price?: number;
    reference?: string;
    vat?: VATValue;
    piece_price?: number;
    discounts?: Array<BolDiscount>;
    invoice_id?: string;
    invoice?: Invoice | any;
}

export interface OrderItemFields extends BaseFields {
    item_id: string;
    ean: string;
    title: string;
    quantity: number;
    quantity_shipped: number;
    quantity_cancelled: number;
    price_string: string;
    price: number;
    piece_price_string: string;
    piece_price: number;
    discounts: Array<BolDiscount>;
    reference: string;
    vat: VATValue;
    invoice_id: string;
    invoice: Invoice | null;
}

const fields: OrderItemFields = {
    id: '',
    created_at: '',
    updated_at: '',
    item_id: '',
    ean: '',
    title: '',
    quantity: 0,
    quantity_shipped: 0,
    quantity_cancelled: 0,
    price: 0,
    price_string: '0',
    piece_price: 0,
    piece_price_string: '0',
    discounts: [],
    reference: '',
    vat: DefaultVAT(0),
    invoice_id: '',
    invoice: null
};

const fieldsDefinition: {[key in keyof Omit<OrderItemFields, 'invoice'>]: FieldDefinition} = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    item_id: createFieldDef(ModelFieldType.STRING),
    ean: createFieldDef(ModelFieldType.STRING),
    title: createFieldDef(ModelFieldType.STRING),
    quantity: createFieldDef(ModelFieldType.NUMBER),
    quantity_cancelled: createFieldDef(ModelFieldType.NUMBER),
    quantity_shipped: createFieldDef(ModelFieldType.NUMBER),
    price: createFieldDef(ModelFieldType.NUMBER),
    price_string: createFieldDef(ModelFieldType.STRING, '0'),
    reference: createFieldDef(ModelFieldType.STRING),
    vat: createFieldDef(ModelFieldType.OBJECT, DefaultVAT(0)),
    invoice_id: createFieldDef(ModelFieldType.STRING),
    piece_price: createFieldDef(ModelFieldType.NUMBER),
    piece_price_string: createFieldDef(ModelFieldType.STRING, '0'),
    discounts: createFieldDef(ModelFieldType.ARRAY)
};

const ignoreProps: Array<keyof Props> = ['price'];

export default class OrderItem extends BaseModel<Props, OrderItemFields> {
    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id as string;
    }

    get itemId(): string {
        return this.fields.item_id;
    }

    set itemId(item_id: string) {
        this.fields.item_id = item_id;
    }

    get ean(): string {
        return this.fields.ean;
    }

    set ean(ean: string) {
        this.fields.ean = ean;
    }

    get title(): string {
        return this.fields.title;
    }

    set title(title: string) {
        this.fields.title = title;
    }

    get quantity(): number {
        return this.fields.quantity;
    }

    set quantity(quantity: number) {
        this.fields.quantity = quantity;
    }

    get quantityShipped(): number {
        return this.fields.quantity_shipped || this.fields.quantity;
    }

    get quantityCancelled(): number {
        return this.fields.quantity_cancelled;
    }

    get quantityNotCancelled(): number {
        return this.fields.quantity - this.fields.quantity_cancelled;
    }

    get priceString(): string {
        return this.fields.price_string;
    }

    set priceString(priceString: string) {
        this.fields.price_string = priceString;
        priceString = priceString.replace(',', '.');
        if (!Number.isNaN(priceString)) this.fields.price = Number(priceString);
    }

    get price(): number {
        const priceString = this.priceString.replace(',', '.');
        let price: number;
        if (!Number.isNaN(priceString)) price = Number(priceString);
        else price = this.fields.price;
        return price;
    }

    set price(price: number) {
        this.fields.price = price;
        this.priceString = price.toString();
    }

    get subTotalExcl(): number {
        return this.piecePriceExcl * this.quantity;
    }

    get totalExcl(): number {
        const isNegative = this.price < 0;
        const total = calculateExclVAT(Math.abs(this.price), this.vat).numberValue;
        if (isNegative) return ensureNegative(total);
        return total;
    }

    getTotalNotCancelledExcl = (subtractDiscounts: boolean = true): number => {
        let total = this.totalNotCancelledExcl;
        if (subtractDiscounts) total -= this.totalDiscountExcl;
        return total;
    };

    get totalNotCancelledExcl(): number {
        return this.quantityNotCancelled * this.piecePriceExcl;
    }

    get piecePrice(): number {
        const piecePrice = this.piecePriceString.replace(',', '.');
        if (!Number.isNaN(Number(piecePrice))) return Number(piecePrice);
        return this.fields.piece_price;
    }

    set piecePrice(piecePrice: number) {
        this.fields.piece_price = piecePrice;
        this.fields.piece_price_string = piecePrice.toString();
    }

    get piecePriceExcl(): number {
        const isNegative = this.piecePrice < 0;
        const priceExcl = calculateExclVAT(Math.abs(this.piecePrice), this.vat).numberValue;
        if (isNegative) return ensureNegative(priceExcl);
        return priceExcl;
    }

    get piecePriceString(): string {
        return this.fields.piece_price_string;
    }

    set piecePriceString(piecePrice: string) {
        this.fields.piece_price_string = piecePrice;
        piecePrice = piecePrice.replace(',', '.');
        if (!Number.isNaN(piecePrice)) this.fields.piece_price = Number(piecePrice);
    }

    get discounts(): Array<BolDiscount> {
        return this.fields.discounts;
    }

    get totalDiscountExcl(): number {
        return sum(...this.fields.discounts.map((discount: BolDiscount) => discount.amount_excl));
    }

    get totalDiscountIncl(): number {
        return sum(...this.fields.discounts.map((discount: BolDiscount) => discount.amount_incl));
    }

    get reference(): string {
        return this.fields.reference;
    }

    set reference(reference: string) {
        this.fields.reference = reference;
    }

    get vat(): VATValue {
        return this.fields.vat;
    }

    set vat(vat: VATValue) {
        this.fields.vat = vat;
    }

    getNotCancelledTotalVAT = (subtractDiscounts: boolean = true): number => {
        const {vat, piecePrice} = this;
        if ([VATType.MARGIN, VATType.REVERSED, VATType.KOR].includes(vat.vat_type)) return 0;
        const quantity = this.quantityNotCancelled;
        if (vat.vat_type === VATType.SPLIT) return sum(...Object.values((vat as SplitVATValue).value)) * quantity;

        // Default VAT
        let totalPrice = piecePrice * quantity;
        if (subtractDiscounts) totalPrice -= this.totalDiscountIncl;
        return (totalPrice / (100 + vat.value)) * vat.value;
    };

    get notCancelledTotalVat(): number {
        const {vat, piecePrice} = this;
        if ([VATType.MARGIN, VATType.REVERSED, VATType.KOR].includes(vat.vat_type)) return 0;
        const quantity = this.quantityNotCancelled;
        if (vat.vat_type === VATType.SPLIT) return sum(...Object.values((vat as SplitVATValue).value)) * quantity;

        // Default VAT
        return ((piecePrice * quantity) / (100 + vat.value)) * vat.value;
    }

    get invoiceId(): string {
        return this.fields.invoice_id;
    }

    set invoiceId(invoice_id: string) {
        this.fields.invoice_id = invoice_id;
    }

    get invoice(): Invoice | null {
        return this.fields.invoice;
    }

    set invoice(invoice: Invoice | null) {
        this.fields.invoice = invoice;
        if (invoice) this.invoiceId = invoice.id;
    }

    get isCancelled(): boolean {
        return this.quantity > 0 && this.quantity === this.quantityCancelled;
    }

    get hasMargin(): boolean {
        return this.fields.vat.vat_type === VATType.MARGIN;
    }

    getSplitVAT = (): Record<string, number> => {
        if (this.vat.vat_type === VATType.SPLIT) return this.vat.value;
        return {};
    };

    clone = (deep: boolean = true): OrderItem => {
        let fields = this.fields;
        if (deep) fields = deepClone(fields);
        return new OrderItem(fields);
    };

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
            if (props.invoice) {
                const invoice: Invoice = props.invoice instanceof Invoice ? props.invoice : new Invoice(props.invoice);
                invoice.addItem(this);
                this.invoice = invoice;
            }
            if (props.price !== undefined) {
                const price = props.price;
                this.fields.price_string = price.toString();
                this.fields.price = price;
                if (props.piece_price !== undefined) {
                    this.fields.piece_price = props.piece_price;
                    this.fields.piece_price_string = props.piece_price.toString();
                } else if (props.quantity !== undefined) {
                    this.fields.piece_price = props.price / props.quantity;
                    this.fields.piece_price_string = this.fields.piece_price.toString();
                }
            }
        }
    };
}

type CreditProps = {
    orderItem: OrderItem;
    quantity: NumberString;
    percentage?: NumberString;
    id: string;
    vatValue: VATValue;
    priceIncl: NumberString;
};

type CreditFields = {
    priceIncl: NumberString;
    quantity: NumberString;
    orderItem: OrderItem | null;
    id: string;
    title: string;
    percentage: NumberString;
};

export class CreditItem {
    private fields: CreditFields = {
        priceIncl: new NumberString(0),
        quantity: new NumberString(0),
        orderItem: null,
        id: '',
        title: '',
        percentage: new NumberString(100)
    };

    readonly vatValue: VATValue;

    constructor(props: CreditProps) {
        const {quantity, orderItem, vatValue, id, priceIncl} = props;
        this.vatValue = vatValue;
        this.fields.quantity = quantity;
        this.fields.orderItem = orderItem;
        this.fields.id = id;
        this.fields.priceIncl = roundNumberString(priceIncl);
    }

    get priceIncl(): NumberString {
        return this.fields.priceIncl;
    }

    set priceIncl(price: NumberString) {
        if (this.orderItem) {
            this.fields.percentage = new NumberString((price.numberValue / this.orderItem.piecePrice) * 100);
        }
        this.fields.priceIncl = price;
    }

    get percentage(): NumberString {
        return this.fields.percentage;
    }

    set percentage(percentage: NumberString) {
        if (this.orderItem)
            this.fields.priceIncl = new NumberString((this.orderItem.piecePrice / 100) * percentage.numberValue);
        this.fields.percentage = percentage;
    }

    get submitPrice(): number {
        return roundToNumber(this.priceIncl.numberValue * this.quantity.numberValue, 2);
    }

    get quantity(): NumberString {
        return this.fields.quantity;
    }

    set quantity(quantity: NumberString) {
        this.fields.quantity = quantity;
    }

    get orderItem(): OrderItem | null {
        return this.fields.orderItem;
    }

    get id(): string {
        return this.fields.id;
    }

    set id(id: string) {
        this.fields.id = id;
    }

    get title(): string {
        return this.fields.title;
    }

    set title(title: string) {
        this.fields.title = title;
    }

    get subTotalIncl(): number {
        return ensureNegative(this.priceIncl.numberValue * this.quantity.numberValue);
    }

    get vatAmount(): number {
        return (
            roundToNumber(calculateVATAmount(this.vatValue, this.priceIncl).numberValue, 2) * this.quantity.numberValue
        );
    }

    setPercentage = (percentage: NumberString) => {
        if (!this.orderItem) return;
        const originalPrice = this.orderItem.piecePriceExcl;
        this.priceIncl = new NumberString(originalPrice * (percentage.numberValue / 100));
    };
}
